import { createAppAsyncThunk } from '@/store/typedAsyncThunk';
import { getApiEndpoints } from '@/services/api/endpoints';
import { isNotFoundError } from '@/lib';
import {
  DEFAULT_MOBILE_APP_CONFIG,
  type FetchSystemSettingsResult,
  type MobileAppConfig,
  type UpdateSettingsRequest,
  type UpdateSettingsResponse,
} from './mobileConfigTypes';

// Fetch the operational remote and top-level subscription settings.
export const fetchSystemSettings = createAppAsyncThunk<FetchSystemSettingsResult, void>(
  'settings/fetchSystemSettings',
  async (_, { extra: { config }, rejectWithValue }) => {
    const { apiService, logger } = config;

    try {
      const endpoint = getApiEndpoints().core.adminMobileConfig.getMobileConfig;
      logger?.info('[fetchSystemSettings] Fetching system settings', { endpoint });

      const response = await apiService.get<MobileAppConfig>(endpoint);
      logger?.debug('[fetchSystemSettings] Response received');

      return { settings: response, hydratedFromDefaults: false };
    } catch (err) {
      const error = err as Error & { status?: number; body?: string };
      // ---- CASE 1: settings not found → return defaults ----
      if (isNotFoundError(error)) {
        logger?.info('[fetchSystemSettings] No settings found, using defaults');
        return { settings: DEFAULT_MOBILE_APP_CONFIG, hydratedFromDefaults: true };
      }

      // ---- CASE 2: real error ----
      logger?.error('[fetchSystemSettings] Failed to fetch system settings', {
        message: error.message,
        status: error.status,
        body: error.body,
      });
      return rejectWithValue({ message: error.message || 'Failed to fetch settings' });
    }
  },
);

// Update operational and/or subscription settings - Super Admin only.
export const updateSystemSettings = createAppAsyncThunk<UpdateSettingsResponse, UpdateSettingsRequest>(
  'settings/updateSystemSettings',
  async (request, { extra: { config }, rejectWithValue }) => {
    const { apiService, logger } = config;

    try {
      const endpoint = getApiEndpoints().core.adminMobileConfig.updateMobileConfig;

      const updateKeys = Object.keys(request.updates ?? {});
      logger?.info('[updateSystemSettings] Updating system settings', {
        updateKeys: updateKeys.join(', ') || '(none)',
      });

      const response = await apiService.put<UpdateSettingsResponse, UpdateSettingsRequest>(endpoint, request);
      logger?.debug('[updateSystemSettings] Response received');

      return response;
    } catch (err) {
      const error = err as Error & { status?: number; body?: string };
      logger?.error('[updateSystemSettings] Failed to update system settings', {
        message: error.message,
        status: error.status,
        body: error.body,
      });

      let errorMessage = error.message;
      if (error.body) {
        try {
          const body = JSON.parse(error.body);
          errorMessage = body.message || error.message;
        } catch {
          // keep original message
        }
      }

      if (error.status === 403) {
        errorMessage = "You don't have permission to update settings";
      }

      return rejectWithValue({ message: errorMessage });
    }
  },
);
